/**
 * Kanopy Payment Token JS integration.
 * Mirrors acceptJs.ts but for the Kanopy gateway.
 *
 * How it works:
 * - The Token.js script is loaded as a <script> tag with device config as HTML attributes.
 * - The script injects card input fields into divs: #ccNumber, #ccExpDate, #cvv
 * - Tokenization is triggered by clicking <button id="paymentTokenButton">
 * - The result is delivered via gatewayTokenJS.callback({ token, cardType, last4 })
 *
 * For delivery orders (dual-gateway), the script is loaded twice:
 *   once with the merchant device GUID, once with the Basil device GUID.
 */

// Kanopy URLs — configurable via Vite env variables, with sensible defaults
const SANDBOX_URL = (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_KANOPY_SANDBOX_TOKENJS_URL)
    || "https://websandbox.mykanopy.com/assets/payment/Token.js";
const PROD_URL = (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_KANOPY_PROD_TOKENJS_URL)
    || "https://payments.mykanopy.com/assets/payment/Token.js";
const SANDBOX_API_URL = (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_KANOPY_SANDBOX_API_URL)
    || "https://apisandbox.mykanopy.com/api/v1";
const PROD_API_URL = (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_KANOPY_PROD_API_URL)
    || "https://api.mykanopy.com/api/v1";
const SCRIPT_ID   = "gtwPayment";

/** Tracks whether Token.js has been loaded at least once in this page session.
 *  Used to avoid re-adding the script tag (which causes "GtwTokenJS already declared"). */
let tokenJsLoaded = false;

/**
 * Reads a Token.js-injected card field value from the DOM.
 * Uses multiple selector strategies to be resilient against Token.js DOM changes.
 */
function readCardField(containerId: string): string {
    const container = document.getElementById(containerId);
    if (!container) return "";

    // Strategy 1: direct input child (current Token.js behavior)
    const input = container.querySelector("input") as HTMLInputElement | null;
    if (input?.value) return input.value;

    // Strategy 2: input with common type attributes
    const typed = container.querySelector("input[type='text'], input[type='tel'], input[type='number']") as HTMLInputElement | null;
    if (typed?.value) return typed.value;

    // Strategy 3: any contenteditable or textarea
    const editable = container.querySelector("[contenteditable='true']") as HTMLElement | null;
    if (editable?.textContent) return editable.textContent;

    return "";
}

/** Returns true if the Kanopy card number field has a value (used for hasCard checks). */
export function hasKanopyCardValue(): boolean {
    return !!readCardField("ccNumber").trim();
}

/** Token data returned by the Kanopy callback */
export interface KanopyTokenData {
    token: string;
    cardType: string;
    last4: string;
}

/**
 * True when the merchant Kanopy call should use the sandbox URL.
 * Mirrors shouldUseSandboxForMerchant() in acceptJs.ts.
 * Basil always uses production regardless of this flag.
 */
function shouldUseSandbox(): boolean {
    if (typeof import.meta !== "undefined") {
        const env = (import.meta as any).env;
        if (env?.DEV === true) return true;
        if (String(env?.VITE_ACCEPT_JS_MERCHANT_SANDBOX || "").toLowerCase() === "true") return true;
        if (String(env?.VITE_APP_ENV || "").toLowerCase() === "development") return true;
    }
    if (typeof window !== "undefined" && window.location?.hostname) {
        const h = window.location.hostname.toLowerCase();
        if (h === "localhost" || h === "127.0.0.1") return true;
        if (h.includes("dev")) return true;
    }
    return false;
}

/**
 * Dynamically inject the Token.js <script> tag configured for the given device.
 * The script injects card fields into #ccNumber, #ccExpDate, #cvv in the DOM.
 *
 * If Token.js is already loaded (script tag exists), it updates the credentials
 * on the existing tag and re-dispatches DOMContentLoaded to reinitialize —
 * this avoids the "Identifier 'GtwTokenJS' has already been declared" error
 * that occurs when the script file is loaded twice.
 *
 * @param deviceGuid  Kanopy device GUID (merchant or Basil)
 * @param apiKey      Kanopy API key for the device
 * @param options     useProdUrl: true forces production URL (Basil always prod)
 */
export function loadKanopyTokenJs(
    deviceGuid: string,
    apiKey: string,
    options?: { useProdUrl?: boolean }
): Promise<void> {
    if (typeof window === "undefined") {
        return Promise.reject(new Error("Kanopy Token.js requires a browser environment."));
    }
    if (!deviceGuid || !apiKey) {
        return Promise.reject(new Error("Kanopy deviceGuid and apiKey are required to load Token.js."));
    }

    const useProd = options?.useProdUrl === true || !shouldUseSandbox();
    const env = useProd ? "production" : "sandbox";
    // If Token.js was already loaded successfully, re-dispatch DOMContentLoaded so it
    // re-injects card fields into any newly mounted div containers (#ccNumber, #ccExpDate, #cvv).
    // This handles the case where KanopyPaymentForm unmounts (user selects saved card) and
    // re-mounts (user clicks "Add Card") - the divs are new but Token.js already ran.
    if (tokenJsLoaded && (window as any).gatewayTokenJS) {
        document.dispatchEvent(new Event("DOMContentLoaded"));
        return Promise.resolve();
    }

    const src = useProd ? PROD_URL : SANDBOX_URL;

    // Remove stale script tag if it exists (failed previous load)
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) existing.remove();
    try { (window as any).gatewayTokenJS = undefined; } catch { /* ignore */ }

    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.src = src;
        script.setAttribute("user-api-key", apiKey);
        script.setAttribute("merch-device-guid", deviceGuid);
        script.setAttribute("merch-style-class", "form-control");
        script.setAttribute("sys-environment", env);

        script.onload = () => {
            // Poll until gatewayTokenJS global is set by the script
            let attempts = 0;
            const interval = setInterval(() => {
                if ((window as any).gatewayTokenJS) {
                    clearInterval(interval);

                    // Token.js wraps ALL its initialization (field injection + button wiring)
                    // inside a DOMContentLoaded listener. When the script is dynamically
                    // injected that event has already fired, so we dispatch it again now to
                    // trigger Token.js's handler and inject the card input fields.
                    document.dispatchEvent(new Event("DOMContentLoaded"));

                    tokenJsLoaded = true;
                    resolve();
                } else if (++attempts > 200) {
                    clearInterval(interval);
                    reject(new Error(
                        "Kanopy Token.js loaded but gatewayTokenJS global was not set. " +
                        "Check that the deviceGuid and apiKey are valid."
                    ));
                }
            }, 50);
        };
        script.onerror = () => reject(new Error(
            `Kanopy Token.js failed to load from ${src}. ` +
            `Ensure ${new URL(src).hostname} is allowed in Content-Security-Policy script-src.`
        ));
        document.head.appendChild(script);
    });
}

/**
 * Trigger tokenization by programmatically clicking the hidden #paymentTokenButton.
 * Requires:
 *  - loadKanopyTokenJs() to have completed (gatewayTokenJS is set)
 *  - KanopyPaymentForm to be mounted (div#ccNumber, #ccExpDate, #cvv in DOM)
 *  - The user to have filled in the injected card fields
 */
export function triggerKanopyTokenization(): Promise<KanopyTokenData> {
    return new Promise((resolve, reject) => {
        const gtwTokenJS = (window as any).gatewayTokenJS;
        if (!gtwTokenJS) {
            reject(new Error(
                "Kanopy Token.js is not loaded. Call loadKanopyTokenJs() first."
            ));
            return;
        }

        gtwTokenJS.callback = (response: {
            token: string | null;
            cardType: string;
            last4: string;
            error?: string;
        }) => {
            if (!response.token) {
                reject(new Error(
                    response.error ?? "Kanopy tokenization failed. Please check your card details and try again."
                ));
                return;
            }
            resolve({
                token: response.token,
                cardType: response.cardType ?? "",
                last4: response.last4 ?? "",
            });
        };

        const btn = document.getElementById("paymentTokenButton") as HTMLButtonElement | null;
        if (!btn) {
            reject(new Error(
                "paymentTokenButton not found in DOM. Ensure KanopyPaymentForm is mounted."
            ));
            return;
        }
        btn.click();
    });
}

/**
 * Calls Kanopy's /tokenization endpoint directly from the browser.
 * Used for the Basil (second) tokenization where Token.js can't be reloaded
 * (reloading causes "GtwTokenJS already declared" SyntaxError).
 *
 * This is PCI-clean: card data goes browser → Kanopy directly, same as Token.js.
 */
async function tokenizeDirectly(
    deviceGuid: string,
    apiKey: string,
    cardNumber: string,
    expirationDate: string,
    cvv: string
): Promise<KanopyTokenData> {
    const baseUrl = shouldUseSandbox() ? SANDBOX_API_URL : PROD_API_URL;

    // Convert MM/YY → YYMM (Kanopy's expected format)
    const stripped = expirationDate.replace(/[\\/\s]/g, "");
    const month = stripped.slice(0, 2);
    const year  = stripped.slice(-2);
    const expFormatted = `${year}${month}`;

    // 30-second timeout — prevents indefinite hang if Kanopy is unresponsive
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30_000);

    let res: Response;
    try {
        res = await fetch(`${baseUrl}/tokenization`, {
            method: "POST",
            signal: controller.signal,
            headers: {
                "Content-Type": "application/json",
                "UserAuthorization": apiKey,
            },
            body: JSON.stringify({
                deviceGuid,
                card: {
                    cardNumber,
                    cvv2: cvv,
                    expirationDate: expFormatted,
                },
            }),
        });
    } catch (e: any) {
        if (e.name === "AbortError") {
            throw new Error("Kanopy tokenization timed out. Please try again.");
        }
        throw new Error(e.message ?? "Kanopy tokenization failed — network error.");
    } finally {
        clearTimeout(timeoutId);
    }

    const data = await res.json();
    if (!res.ok || !data?.card?.cardNumber) {
        throw new Error(data?.message ?? "Kanopy tokenization failed.");
    }

    return {
        token: data.card.cardNumber,
        cardType: data.card.cardType ?? "",
        last4: data.card.last4 ?? "",
    };
}

/**
 * Dual tokenization for delivery orders and save-card flows.
 *
 * Step 1: Merchant token via Token.js (already loaded by KanopyPaymentForm).
 * Step 2: Basil token via direct API call to Kanopy's /tokenization endpoint.
 *         Token.js can't be reloaded (class redeclaration error), so we call
 *         the API directly from the browser — still PCI-clean (browser → Kanopy).
 */
export async function tokenizeForDelivery(
    merchantDeviceGuid: string,
    basilDeviceGuid: string,
    merchantApiKey: string,
    basilApiKey: string
): Promise<{ merchantToken: KanopyTokenData; basilToken: KanopyTokenData }> {
    // Step 1: merchant token via Token.js (fields already filled by user)
    const merchantToken = await triggerKanopyTokenization();

    // Step 2: capture card values from Token.js fields (still in DOM after tokenization)
    const ccNum = readCardField("ccNumber");
    const ccExp = readCardField("ccExpDate");
    const cvv   = readCardField("cvv");

    if (!ccNum || !ccExp || !cvv) {
        throw new Error("Card fields are empty — cannot tokenize for Basil gateway.");
    }

    // Step 3: Basil token via direct API call (bypasses Token.js reload issue)
    const basilToken = await tokenizeDirectly(basilDeviceGuid, basilApiKey, ccNum, ccExp, cvv);

    return { merchantToken, basilToken };
}
